/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useTranslation } from "react-i18next";
import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useApiClient } from "common/ApiClientProvider";

function DeleteAccount() {
  const { t } = useTranslation("pages");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleConfirmOpen = () => setConfirmOpen(true);
  const handleConfirmClose = () => setConfirmOpen(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const handleLoadingClose = () => setLoadingOpen(false);
  const handleLoadingOpen = () => setLoadingOpen(true);
  const apiClient = useApiClient();
  async function deleteAccount() {
    handleLoadingOpen();
    try {
      await apiClient.post("delete_account");
    } finally {
      handleLoadingClose();
    }
  }
  return (
    <Card id="delete-account">
      <MDBox
        pr={3}
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loadingOpen}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Modal
          open={confirmOpen}
          onClick={handleConfirmClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <MDBox
            p={5}
            position="absolute"
            color="white"
            bgColor="white"
            transform="translate(-50%, -50%)"
          >
            <MDTypography variant="h5">
              {t("accountSettings.accountDeletionConfirmation")}
            </MDTypography>
            <br />
            <Stack spacing={5} direction="row">
              <MDButton
                p={5}
                variant="gradient"
                color="dark"
                onClick={() => {
                  deleteAccount();
                  handleConfirmClose();
                }}
              >
                {t("accountSettings.deletionOptionDelete")}
              </MDButton>
              <MDButton p={5} variant="gradient" color="dark" onClick={handleConfirmClose}>
                {t("accountSettings.deletionOptionCancel")}
              </MDButton>
            </Stack>
          </MDBox>
        </Modal>
        <MDBox p={3} lineHeight={1}>
          <MDBox mb={1}>
            <MDTypography variant="h5">{t("accountSettings.deleteAccount")}</MDTypography>
          </MDBox>
          <MDTypography variant="body2" color="text" fontWeight="bold">
            {t("accountSettings.accountDeletionProcess")}
          </MDTypography>
          <MDBox component="ol" m={0} mt={0.5} pl={3}>
            <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
              {t("accountSettings.deletionStep1")}
            </MDTypography>
            <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
              {t("accountSettings.deletionStep2")}
            </MDTypography>
            <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
              {t("accountSettings.deletionStep3")}
              <MDBox component="ul" m={0} mt={0.25} pl={3}>
                <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
                  {t("accountSettings.deletionOptionDelete")}
                </MDTypography>
                <MDTypography component="li" variant="body2" color="text" lineHeight={1.6}>
                  {t("accountSettings.deletionOptionCancel")}
                </MDTypography>
              </MDBox>
            </MDTypography>
          </MDBox>
          <MDBox mt={2} ml="auto">
            <MDButton variant="gradient" color="error" size="small" onClick={handleConfirmOpen}>
              {t("accountSettings.deleteAccountAction")}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default DeleteAccount;
